import { useState } from 'react';
import { GhostButton, LogoMark, PrimaryButton, Screen } from '../components/Chrome.js';
import {
  firebaseEnabled,
  playAsGuest,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
  type AuthUser,
} from '../lib/firebase.js';
import { color } from '../theme.js';

export function AuthScreen({ onAuth }: { onAuth: (u: AuthUser) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<AuthUser>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onAuth(await fn());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
      setBusy(false);
    }
  };

  const fieldStyle = (filled: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '15px 16px',
    border: `1px solid ${filled ? 'rgba(166,255,46,.28)' : 'rgba(234,255,234,.14)'}`,
    borderRadius: 11,
    background: filled ? 'rgba(166,255,46,.05)' : 'rgba(255,255,255,.03)',
  });

  return (
    <Screen brackets="diagonal">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(16px,5cqw,24px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <div style={{ width: 'min(100%,440px)', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <LogoMark size={64} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontWeight: 800, fontSize: 'clamp(24px,8cqw,30px)', letterSpacing: '.01em', whiteSpace: 'nowrap' }}>
                <span style={{ color: color.lime }}>Skill</span>Issue<span style={{ color: 'rgba(234,255,234,.55)' }}>Club</span>
              </span>
              <span style={{ fontSize: 11, letterSpacing: '.28em', fontWeight: 600, color: 'rgba(166,255,46,.6)' }}>
                SIGN IN TO COMPETE
              </span>
            </div>
          </div>

          {!firebaseEnabled && (
            <div
              style={{
                padding: '10px 12px',
                border: '1px solid rgba(255,176,32,.3)',
                borderRadius: 9,
                background: 'rgba(255,176,32,.06)',
                fontSize: 12,
                color: color.amberBright,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Dev mode — Firebase not configured. Any credentials work.
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(() => (mode === 'signin' ? signInWithEmail(email, password) : signUpWithEmail(email, password)));
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, letterSpacing: '.2em', fontWeight: 600, color: 'rgba(234,255,234,.45)' }}>EMAIL</span>
              <div style={fieldStyle(email.length > 0)}>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="player@race.gg"
                  style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontFamily: "'JetBrains Mono',monospace", fontSize: 15, color: 'rgba(234,255,234,.9)' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 11, letterSpacing: '.2em', fontWeight: 600, color: 'rgba(234,255,234,.45)' }}>PASSWORD</span>
              <div style={fieldStyle(password.length > 0)}>
                <input
                  type="password"
                  required
                  minLength={6}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontFamily: "'JetBrains Mono',monospace", fontSize: 15, letterSpacing: '.1em', color: 'rgba(234,255,234,.9)' }}
                />
              </div>
            </div>

            {error && (
              <div style={{ fontSize: 12, color: color.redBright, textAlign: 'center' }}>{error}</div>
            )}

            <PrimaryButton disabled={busy} style={{ padding: 17, fontSize: 17 }}>
              {busy ? 'WORKING…' : mode === 'signin' ? 'SIGN IN' : 'CREATE ACCOUNT'}
            </PrimaryButton>
          </form>

          <button
            onClick={() => setMode((m) => (m === 'signin' ? 'signup' : 'signin'))}
            style={{ fontSize: 12, color: 'rgba(234,255,234,.45)', textAlign: 'center' }}
          >
            {mode === 'signin' ? (
              <>
                No account? <span style={{ color: color.lime }}>Create one</span>
              </>
            ) : (
              <>
                Have an account? <span style={{ color: color.lime }}>Sign in</span>
              </>
            )}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(234,255,234,.12)' }} />
            <span style={{ fontSize: 11, letterSpacing: '.16em', color: 'rgba(234,255,234,.4)', fontWeight: 600 }}>OR</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(234,255,234,.12)' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            <GhostButton onClick={() => void run(signInWithGoogle)} style={{ gap: 11, padding: 15 }}>
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: 'conic-gradient(#ea4335 0 25%,#fbbc05 0 50%,#34a853 0 75%,#4285f4 0 100%)',
                  display: 'inline-block',
                  marginRight: 10,
                }}
              />
              <span style={{ fontWeight: 600, fontSize: 15 }}>Continue with Google</span>
            </GhostButton>

            <button
              onClick={() => void run(playAsGuest)}
              disabled={busy}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
                padding: 15,
                border: '1px dashed rgba(166,255,46,.35)',
                borderRadius: 12,
                background: 'rgba(166,255,46,.05)',
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: '.04em', color: color.limeBright }}>PLAY AS GUEST</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: 'rgba(166,255,46,.6)' }}>NO ACCOUNT</span>
            </button>
          </div>

          <span style={{ textAlign: 'center', fontSize: 11, color: 'rgba(234,255,234,.35)', lineHeight: 1.5 }}>
            Guests get a provisional rank — <span style={{ color: color.lime }}>link later</span> to keep it.
          </span>
        </div>
      </div>
    </Screen>
  );
}
